import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as d3 from 'd3';
import { feature, merge, mesh } from 'topojson-client';
import type { GeometryCollection, Topology } from 'topojson-specification';
import { world as lists } from '../lists';

type TryTrueLinks = { try: string; true: string };

type WorldAtlasObjects = {
  countries: GeometryCollection;
};

type WorldAtlas = Topology<WorldAtlasObjects>;

type RegionKind = 'usa' | 'city' | 'regional' | 'country';

type RegionDef = {
  slug: string;
  name: string;
  kind: RegionKind;
  links: TryTrueLinks;
};

// Region colors — earthy pigments rather than screen primaries, so the map
// reads like a printed atlas against the paper background.
const REGION_COLORS: Record<string, string> = {
  // US Regions
  Northeast: '#3a6491', // slate blue
  Southeast: '#2f7d5f', // pine
  Midwest: '#d19a2e', // ochre
  Southwest: '#c05a34', // brick
  West: '#7a5aa8', // plum

  // Multi-country regions
  'Middle East': '#2b8a9e', // deep teal
  'Western Europe': '#7d9440', // olive
  Mediterranean: '#d97534', // terracotta
  'South America': '#b8446b', // raspberry
  'Southeast Asia': '#4d5bab', // indigo

  // Specific countries
  Australia: '#2f9182', // sea green
  Canada: '#3d7f5c', // fern
  Mexico: '#b83a2e', // chili
  Japan: '#7b3a2f', // aubergine

  // Cities
  Seattle: '#c2410c',
  Portland: '#c2410c',
  'Bay Area': '#c2410c',
  NYC: '#c2410c',
};

// Everything that isn't the selected region drops to these when a selection is active
const PANEL_WIDTH = 340;
// Share of the viewport height the map keeps on mobile; the panel takes the rest
const MOBILE_MAP_FRACTION = 0.52;

// Printed-atlas palette (mirrors the tokens in index.css)
const PAPER = '#efe5d4';
const OCEAN = '#fdfaf2';
const LAND = '#e0d4bd';
const LAND_USA = '#d8cab0';
const HAIRLINE = '#fffdf8';
const DIM_FILL = '#ded3c0';
const DIM_STROKE = '#cabba2';
const HIGHLIGHT = '#f2b705';
const PANEL = '#fbf7ef';
const RULE = '#ded1ba';
const INK = '#221e19';
const INK_FAINT = '#9a8d7b';
const ACCENT = '#c2410c'; // burnt orange — the "try" side
const GREEN = '#166534'; // deep green — the "true" side

const KIND_LABELS: Record<RegionKind, string> = {
  usa: 'U.S. region',
  city: 'City list',
  regional: 'Regional list',
  country: 'Country list',
};

// Section headings for the region index at the foot of the panel
const KIND_GROUPS: { kind: RegionKind; label: string }[] = [
  { kind: 'city', label: 'Cities' },
  { kind: 'usa', label: 'United States' },
  { kind: 'regional', label: 'Regions' },
  { kind: 'country', label: 'Countries' },
];

const COUNTRY_NAME_MAP: Record<string, string> = {
  // Map TopoJSON country names to keys used in lists.ts under 'Specific Countries'
  Australia: 'Australia',
  Canada: 'Canada',
  Mexico: 'Mexico',
  Japan: 'Japan',
  'United States of America': 'USA',
};

// US regions to state names (must match us-atlas state names)
const US_REGION_TO_STATES: Record<string, string[]> = {
  Northeast: [
    'Connecticut',
    'Delaware',
    'Maine',
    'Maryland',
    'Massachusetts',
    'New Hampshire',
    'New Jersey',
    'New York',
    'Pennsylvania',
    'Rhode Island',
    'Vermont',
  ],
  Southeast: [
    'Alabama',
    'Arkansas',
    'Florida',
    'Georgia',
    'Kentucky',
    'Louisiana',
    'Mississippi',
    'North Carolina',
    'South Carolina',
    'Tennessee',
    'Virginia',
    'West Virginia',
  ],
  Midwest: [
    'Illinois',
    'Indiana',
    'Iowa',
    'Kansas',
    'Michigan',
    'Minnesota',
    'Missouri',
    'Nebraska',
    'North Dakota',
    'Ohio',
    'South Dakota',
    'Wisconsin',
  ],
  Southwest: ['Arizona', 'New Mexico', 'Oklahoma', 'Texas'],
  West: [
    'Alaska',
    'California',
    'Colorado',
    'Hawaii',
    'Idaho',
    'Montana',
    'Nevada',
    'Oregon',
    'Utah',
    'Washington',
    'Wyoming',
  ],
};

// Multi-country region membership (TopoJSON country names)
const REGION_TO_COUNTRIES: Record<string, string[]> = {
  'Middle East': [
    'Afghanistan',
    'Bahrain',
    'Cyprus',
    'Egypt',
    'Iran',
    'Iraq',
    'Israel',
    'Jordan',
    'Kuwait',
    'Lebanon',
    'Oman',
    'Pakistan',
    // TopoJSON may have 'Palestine' as 'Palestine'
    'Palestine',
    'Qatar',
    'Saudi Arabia',
    'Syria',
    'Turkey',
    'United Arab Emirates',
    'Yemen',
  ],
  'Western Europe': [
    'Iceland',
    'Ireland',
    'United Kingdom', // user wrote "the UK"
    'Portugal',
    'Spain',
    'France',
    'Belgium',
    'Netherlands',
    'Germany',
    'Denmark',
    'Sweden',
    'Norway',
    'Finland',
    'Switzerland',
    'Austria',
    'Czechia',
  ],
  Mediterranean: [
    'Italy',
    'Slovenia',
    'Croatia',
    'Montenegro',
    'Albania',
    'Greece',
    'Morocco',
  ],
  'South America': [
    'Argentina',
    'Bolivia',
    'Brazil',
    'Chile',
    'Colombia',
    'Ecuador',
    'Guyana',
    'Paraguay',
    'Peru',
    'Suriname',
    'Uruguay',
    'Venezuela',
  ],
  'Southeast Asia': [
    'Brunei',
    'Cambodia',
    'China',
    'Indonesia',
    'Japan',
    'Laos',
    'Malaysia',
    'Mongolia',
    'Myanmar',
    'North Korea',
    'Philippines',
    'Singapore',
    'South Korea',
    'Taiwan',
    'Thailand',
    'Timor-Leste',
    'Vietnam',
  ],
};

const CITIES: {
  name: string;
  lat: number;
  lon: number;
  links: TryTrueLinks;
}[] = [
  {
    name: 'Seattle',
    lat: 47.61,
    lon: -122.33,
    links: (lists.Cities as any)['Seattle'],
  },
  {
    name: 'Portland',
    lat: 45.52,
    lon: -122.68,
    links: (lists.Cities as any)['Portland'],
  },
  {
    name: 'Bay Area',
    lat: 37.77,
    lon: -122.42,
    links: (lists.Cities as any)['Bay Area'],
  },
  { name: 'NYC', lat: 40.71, lon: -74.01, links: (lists.Cities as any)['NYC'] },
];

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

// Every selectable region, keyed by the slug that appears in ?region=
const REGIONS: RegionDef[] = [
  ...Object.entries(lists.USA).map(([name, links]) => ({
    slug: slugify(name),
    name,
    kind: 'usa' as const,
    links: links as TryTrueLinks,
  })),
  ...Object.entries(lists.Cities).map(([name, links]) => ({
    slug: slugify(name),
    name,
    kind: 'city' as const,
    links: links as TryTrueLinks,
  })),
  ...Object.entries(lists.Regional).map(([name, links]) => ({
    slug: slugify(name),
    name,
    kind: 'regional' as const,
    links: links as TryTrueLinks,
  })),
  ...Object.entries(lists['Specific Countries']).map(([name, links]) => ({
    slug: slugify(name),
    name,
    kind: 'country' as const,
    links: links as TryTrueLinks,
  })),
];

const REGION_BY_SLUG: Record<string, RegionDef> = REGIONS.reduce(
  (acc, r) => {
    acc[r.slug] = r;
    return acc;
  },
  {} as Record<string, RegionDef>
);

// Hand-picked view centers ([lon, lat]) - geometric centroids drift on regions
// with far-flung members (Alaska/Hawaii in West, Iceland in Western Europe).
const REGION_CENTERS: Record<string, [number, number]> = {
  west: [-119, 41],
  southwest: [-103, 33],
  midwest: [-92, 42],
  southeast: [-84, 33],
  northeast: [-73, 42],
  mediterranean: [15, 40],
  'western-europe': [5, 50],
  'south-america': [-60, -15],
  'middle-east': [45, 28],
  'southeast-asia': [110, 12],
  australia: [134, -25],
  canada: [-98, 58],
  mexico: [-102, 23],
  japan: [138, 37],
  ...CITIES.reduce(
    (acc, c) => {
      acc[slugify(c.name)] = [c.lon, c.lat] as [number, number];
      return acc;
    },
    {} as Record<string, [number, number]>
  ),
};

function getLinksForCountry(countryName: string): TryTrueLinks | undefined {
  const key = COUNTRY_NAME_MAP[countryName];
  if (!key) return undefined;

  if (key === 'USA') {
    // Special handling will subdivide the US into regions; ask user for mapping.
    // For now, do not expose top-level links for the entire USA.
    return undefined;
  }

  const specific =
    (lists['Specific Countries'] as Record<string, TryTrueLinks>) || {};
  const links = specific[key];
  return links;
}

function getRegionForCountry(countryName: string): string | undefined {
  for (const [region, members] of Object.entries(REGION_TO_COUNTRIES)) {
    if (members.includes(countryName)) {
      return region;
    }
  }
  return undefined;
}

function getRegionForState(stateName: string): string | undefined {
  for (const [regionName, states] of Object.entries(US_REGION_TO_STATES)) {
    if (states.includes(stateName)) {
      return regionName;
    }
  }
  return undefined;
}

function getColorForRegion(regionName: string): string {
  return REGION_COLORS[regionName] || '#e0e0e0';
}

// Which selectable region (slug) a rendered country belongs to, if any
function countryRegionSlug(countryName: string): string | undefined {
  const specific = getLinksForCountry(countryName);
  if (specific) return slugify(COUNTRY_NAME_MAP[countryName]);
  const region = getRegionForCountry(countryName);
  return region ? slugify(region) : undefined;
}

function stateRegionSlug(stateName: string): string | undefined {
  const region = getRegionForState(stateName);
  return region ? slugify(region) : undefined;
}

function readRegionFromUrl(): string | null {
  const slug = new URLSearchParams(window.location.search).get('region');
  return slug && REGION_BY_SLUG[slug] ? slug : null;
}

// Tab icon cycles through the food groups. SVG data URI keeps the glyph crisp at
// any DPI and needs no asset pipeline.
const FAVICON_EMOJI = [
  '🍔',
  '🌮',
  '🌯',
  '🥚',
  '🍜',
  '🍕',
  '🍣',
  '🥐',
  '🦪',
  '🍦',
  '☕',
  '🍷',
];

function emojiFavicon(emoji: string): string {
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">` +
    `<text y="50%" x="50%" dy="0.35em" text-anchor="middle" font-size="88">${emoji}</text>` +
    `</svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

function useCyclingFavicon(intervalMs = 1600) {
  useEffect(() => {
    let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    const previous = link.href;

    // Start somewhere random so two open tabs aren't in lockstep.
    let i = Math.floor(Math.random() * FAVICON_EMOJI.length);
    const paint = () => {
      link!.href = emojiFavicon(FAVICON_EMOJI[i % FAVICON_EMOJI.length]);
    };
    paint();

    const reduced = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)'
    )?.matches;
    if (reduced) return;

    const id = window.setInterval(() => {
      i += 1;
      paint();
    }, intervalMs);
    return () => {
      window.clearInterval(id);
      if (link) link.href = previous;
    };
  }, [intervalMs]);
}

function useWindowSize() {
  const [size, setSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  useEffect(() => {
    function onResize() {
      setSize({ width: window.innerWidth, height: window.innerHeight });
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return size;
}

function useIsMobile() {
  const { width } = useWindowSize();
  return width < 768; // Tailwind's md breakpoint
}

export default function WorldMap() {
  const { width, height } = useWindowSize();
  const isMobile = useIsMobile();
  useCyclingFavicon();
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [selected, setSelected] = useState<string | null>(readRegionFromUrl);
  const [rotation, setRotation] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  const [lastPinchDistance, setLastPinchDistance] = useState(0);

  const region = selected ? REGION_BY_SLUG[selected] : undefined;

  // Latest rotation, readable from inside the animation loop without restarting it
  const rotationRef = useRef(rotation);
  rotationRef.current = rotation;
  const animRef = useRef<number | null>(null);
  // Distinguishes a click from the tail end of a globe drag
  const didDragRef = useRef(false);

  const projection = useMemo(() => {
    const padding = isMobile ? 20 : 50;
    // Center the globe in the area the panel doesn't cover, so a focused
    // region lands in view rather than hidden behind it.
    const viewWidth = isMobile ? width : width - PANEL_WIDTH;
    const viewHeight = isMobile ? height * MOBILE_MAP_FRACTION : height;
    const radius = Math.min(viewWidth, viewHeight) / 2 - padding;
    return d3
      .geoOrthographic()
      .scale(radius * scale)
      .translate([viewWidth / 2, viewHeight / 2])
      .rotate([rotation.x, rotation.y]);
  }, [width, height, rotation, isMobile, scale]);

  const path = useMemo(
    () => d3.geoPath(projection as d3.GeoProjection),
    [projection]
  );

  const [worldData, setWorldData] = useState<{
    countries: GeoJSON.FeatureCollection<GeoJSON.Geometry, any> | null;
    borders: GeoJSON.MultiLineString | null;
    usStates: GeoJSON.FeatureCollection<GeoJSON.Geometry, any> | null;
    regionShapes: Record<string, GeoJSON.MultiPolygon>;
  }>({ countries: null, borders: null, usStates: null, regionShapes: {} });

  useEffect(() => {
    let isActive = true;
    async function load() {
      const mod = await import('world-atlas/countries-110m.json');
      const topojson = mod as unknown as WorldAtlas;
      const countries = feature(topojson, topojson.objects.countries);
      const borders = mesh(
        topojson,
        topojson.objects.countries,
        (a: any, b: any) => a !== b
      ) as unknown as GeoJSON.MultiLineString;
      // Load US states
      const usMod = await import('us-atlas/states-10m.json');
      const usTopo = usMod as unknown as Topology<{
        states: GeometryCollection;
      }>;
      const usStates = feature(
        usTopo,
        usTopo.objects.states
      ) as unknown as GeoJSON.FeatureCollection<GeoJSON.Geometry, any>;

      // Dissolve each region's members into a single outline so the selection
      // highlight traces the region border, not every internal country/state line.
      const regionShapes: Record<string, GeoJSON.MultiPolygon> = {};
      const countryGeoms = topojson.objects.countries.geometries as any[];
      const stateGeoms = usTopo.objects.states.geometries as any[];

      for (const [name, members] of Object.entries(REGION_TO_COUNTRIES)) {
        const geoms = countryGeoms.filter(
          (g) =>
            members.includes(g.properties?.name) &&
            // countries with their own list are their own region
            !getLinksForCountry(g.properties?.name)
        );
        if (geoms.length) {
          regionShapes[slugify(name)] = merge(
            topojson as any,
            geoms
          ) as GeoJSON.MultiPolygon;
        }
      }
      for (const [topoName, key] of Object.entries(COUNTRY_NAME_MAP)) {
        if (key === 'USA') continue;
        const geoms = countryGeoms.filter(
          (g) => g.properties?.name === topoName
        );
        if (geoms.length) {
          regionShapes[slugify(key)] = merge(
            topojson as any,
            geoms
          ) as GeoJSON.MultiPolygon;
        }
      }
      for (const [name, states] of Object.entries(US_REGION_TO_STATES)) {
        const geoms = stateGeoms.filter((g) =>
          states.includes(g.properties?.name)
        );
        if (geoms.length) {
          regionShapes[slugify(name)] = merge(
            usTopo as any,
            geoms
          ) as GeoJSON.MultiPolygon;
        }
      }

      if (isActive)
        setWorldData({
          countries: countries as any,
          borders,
          usStates,
          regionShapes,
        });
    }
    load();
    return () => {
      isActive = false;
    };
  }, []);

  // Keep ?region= in sync with the selection (and support back/forward)
  useEffect(() => {
    const url = new URL(window.location.href);
    if (selected) url.searchParams.set('region', selected);
    else url.searchParams.delete('region');
    const next = url.pathname + url.search + url.hash;
    const current =
      window.location.pathname + window.location.search + window.location.hash;
    if (next !== current) {
      window.history.pushState({ region: selected }, '', next);
    }
  }, [selected]);

  useEffect(() => {
    function onPopState() {
      setSelected(readRegionFromUrl());
    }
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  function stopAnimation() {
    if (animRef.current !== null) {
      cancelAnimationFrame(animRef.current);
      animRef.current = null;
    }
  }

  function animateRotationTo(target: { x: number; y: number }) {
    stopAnimation();
    const start = { ...rotationRef.current };
    // shortest way around the globe
    const dx = ((((target.x - start.x + 180) % 360) + 360) % 360) - 180;
    const dy = target.y - start.y;
    if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) return;

    const duration = 900;
    const startedAt = performance.now();
    function tick(now: number) {
      const p = Math.min(1, (now - startedAt) / duration);
      // easeInOutCubic
      const e = p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;
      setRotation({ x: start.x + dx * e, y: start.y + dy * e });
      animRef.current = p < 1 ? requestAnimationFrame(tick) : null;
    }
    animRef.current = requestAnimationFrame(tick);
  }

  // Spin the globe to whatever is selected (including on first load from a link)
  useEffect(() => {
    if (!selected) return;
    const explicit = REGION_CENTERS[selected];
    const shape = worldData.regionShapes[selected];
    const center = explicit || (shape ? d3.geoCentroid(shape as any) : null);
    if (!center) return;
    animateRotationTo({ x: -center[0], y: -center[1] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, worldData.regionShapes]);

  useEffect(() => stopAnimation, []);

  function selectRegion(slug: string | undefined) {
    if (!slug || !REGION_BY_SLUG[slug]) return;
    setSelected(slug);
  }

  function handleMouseDown(e: React.MouseEvent) {
    stopAnimation();
    didDragRef.current = false;
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
  }

  function handleMouseMove(e: React.MouseEvent) {
    if (!isDragging) return;

    const deltaX = e.clientX - dragStart.x;
    const deltaY = e.clientY - dragStart.y;
    if (Math.abs(deltaX) > 2 || Math.abs(deltaY) > 2) didDragRef.current = true;

    setRotation((prev) => ({
      x: prev.x + deltaX * 0.5,
      y: prev.y - deltaY * 0.5,
    }));

    setDragStart({ x: e.clientX, y: e.clientY });
  }

  function handleMouseUp() {
    setIsDragging(false);
  }

  function handleTouchStart(e: React.TouchEvent) {
    stopAnimation();
    if (e.touches.length === 1) {
      didDragRef.current = false;
      setIsDragging(true);
      setDragStart({ x: e.touches[0].clientX, y: e.touches[0].clientY });
    } else if (e.touches.length === 2) {
      // Start pinch gesture
      const distance = Math.sqrt(
        Math.pow(e.touches[0].clientX - e.touches[1].clientX, 2) +
          Math.pow(e.touches[0].clientY - e.touches[1].clientY, 2)
      );
      setLastPinchDistance(distance);
      setIsDragging(false); // Stop rotation during pinch
    }
  }

  function handleTouchMove(e: React.TouchEvent) {
    if (e.touches.length === 1 && isDragging) {
      const deltaX = e.touches[0].clientX - dragStart.x;
      const deltaY = e.touches[0].clientY - dragStart.y;
      if (Math.abs(deltaX) > 2 || Math.abs(deltaY) > 2)
        didDragRef.current = true;

      setRotation((prev) => ({
        x: prev.x + deltaX * 0.5,
        y: prev.y - deltaY * 0.5,
      }));

      setDragStart({ x: e.touches[0].clientX, y: e.touches[0].clientY });
    } else if (e.touches.length === 2) {
      // Handle pinch-to-zoom
      const distance = Math.sqrt(
        Math.pow(e.touches[0].clientX - e.touches[1].clientX, 2) +
          Math.pow(e.touches[0].clientY - e.touches[1].clientY, 2)
      );

      if (lastPinchDistance > 0) {
        const scaleChange = distance / lastPinchDistance;
        setScale((prev) => {
          const newScale = prev * scaleChange;
          // Limit zoom range
          return Math.max(0.5, Math.min(3, newScale));
        });
      }

      setLastPinchDistance(distance);
    }
  }

  function handleTouchEnd(e: React.TouchEvent) {
    if (e.touches.length === 0) {
      setIsDragging(false);
      setLastPinchDistance(0);
    }
  }

  function handleWheel(e: React.WheelEvent) {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setScale((prev) => {
      const newScale = prev * delta;
      return Math.max(0.5, Math.min(3, newScale));
    });
  }

  function handleClick(type: 'country' | 'state' | 'city', name: string) {
    if (didDragRef.current) return;
    if (type === 'country') selectRegion(countryRegionSlug(name));
    else if (type === 'state') selectRegion(stateRegionSlug(name));
    else selectRegion(slugify(name));
  }

  const selectedShape = selected ? worldData.regionShapes[selected] : undefined;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: PAPER,
        touchAction: 'manipulation',
        overscrollBehavior: 'none',
      }}
    >
      <style>
        {`
        @keyframes marching-ants { to { stroke-dashoffset: -26; } }
        @keyframes selection-glow {
          0%, 100% { opacity: 0.3; stroke-width: 6; }
          50%      { opacity: 0.75; stroke-width: 11; }
        }
        @keyframes accent-sweep { to { background-position: 200% 0; } }
        @keyframes rise {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: none; }
        }

        .country, .state { transition: fill 0.35s ease, stroke 0.35s ease; }
        .sel-glow {
          fill: none; stroke: ${HIGHLIGHT}; stroke-linejoin: round;
          pointer-events: none;
          animation: selection-glow 1.9s ease-in-out infinite;
        }
        .sel-outline {
          fill: none; stroke: ${INK}; stroke-width: 1.6;
          stroke-linejoin: round; stroke-dasharray: 9 4;
          pointer-events: none;
          animation: marching-ants 0.9s linear infinite;
        }
        .panel-accent {
          background: linear-gradient(90deg, ${HIGHLIGHT}, #c2410c, #e8a33d, ${HIGHLIGHT});
          background-size: 200% 100%;
          animation: accent-sweep 3s linear infinite;
        }

        /* Recipe-card links */
        .list-card {
          position: relative;
          display: block;
          /* Never let the flex column squash these -- overflow is hidden, so
             shrinking silently clips the title and description. */
          flex-shrink: 0;
          padding: 16px 46px 16px 18px;
          border: 1px solid var(--rule);
          border-radius: 4px;
          background: #fffdf8;
          text-decoration: none;
          color: var(--ink);
          overflow: hidden;
          transition: transform 0.25s cubic-bezier(0.2, 0.8, 0.3, 1),
            box-shadow 0.25s ease, border-color 0.25s ease;
          animation: rise 0.4s cubic-bezier(0.2, 0.8, 0.3, 1) backwards;
        }
        .list-card::before {
          content: '';
          position: absolute;
          left: 0; top: 0; bottom: 0;
          width: 4px;
          background: var(--card-accent);
          transition: width 0.25s cubic-bezier(0.2, 0.8, 0.3, 1);
        }
        .list-card:hover {
          transform: translateY(-2px);
          border-color: var(--card-accent);
          box-shadow: 0 10px 24px -12px rgba(34, 30, 25, 0.45);
        }
        .list-card:hover::before { width: 7px; }
        .list-card .eyebrow {
          display: block;
          font-size: 10px;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          color: var(--ink-faint);
          margin-bottom: 5px;
        }
        .list-card .card-title {
          display: block;
          font-size: 21px;
          font-weight: 600;
          line-height: 1.15;
        }
        .list-card .card-sub {
          display: block;
          margin-top: 4px;
          font-size: 13px;
          color: var(--ink-soft);
          line-height: 1.4;
        }
        .list-card .arrow {
          position: absolute;
          right: 18px;
          top: 50%;
          margin-top: -9px;
          font-size: 19px;
          color: var(--card-accent);
          transition: transform 0.25s cubic-bezier(0.2, 0.8, 0.3, 1);
        }
        .list-card:hover .arrow { transform: translateX(4px); }

        .clear-btn {
          flex-shrink: 0;
          width: 32px; height: 32px;
          display: flex; align-items: center; justify-content: center;
          border: 1px solid var(--rule);
          border-radius: 50%;
          background: transparent;
          color: var(--ink-soft);
          font-size: 17px;
          line-height: 1;
          cursor: pointer;
          transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
        }
        .clear-btn:hover {
          background: var(--ink);
          border-color: var(--ink);
          color: var(--panel);
        }

        .panel-body { font-size: 15px; line-height: 1.65; color: var(--ink-soft); }
        .panel-body strong { color: var(--ink); font-weight: 600; }
        .panel-body ul { margin: 0; padding-left: 18px; }
        .panel-body li { margin-bottom: 8px; }
        .panel-body li::marker { color: var(--accent); }

        /* Region index at the foot of the panel */
        /* The panel body is a single scroll region -- content and the region
           index scroll together rather than fighting over two scrollbars. */
        .panel-scroll {
          flex: 1;
          min-height: 0;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          padding-bottom: 22px;
        }
        .region-index {
          /* Sits at the foot of the panel when the content is short, and simply
             follows it once the content is long enough to scroll. */
          margin-top: auto;
          flex-shrink: 0;
          padding: 13px 0 0;
          border-top: 1px solid var(--rule);
        }
        .chip-row {
          display: flex; flex-wrap: wrap; gap: 4px;
          margin: 0;
        }
        .chip-group-label {
          font-size: 8.5px; letter-spacing: 0.16em; text-transform: uppercase;
          color: var(--ink-faint);
          margin: 18px 0 7px;
        }
        /* first group sits flush under the divider */
        .region-index > div:first-child .chip-group-label { margin-top: 0; }
        .chip {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 2px 8px 2px 6px;
          border: 1px solid var(--rule);
          border-radius: 999px;
          background: transparent;
          color: var(--ink-soft);
          font: inherit; font-size: 12px; line-height: 1.5;
          cursor: pointer;
          white-space: nowrap;
          transition: background 0.18s ease, color 0.18s ease,
            border-color 0.18s ease;
        }
        .chip::before {
          content: ''; width: 7px; height: 7px; border-radius: 50%;
          background: var(--chip-color); flex-shrink: 0;
        }
        .chip:hover { border-color: var(--chip-color); color: var(--ink); }
        .chip[aria-current='true'] {
          background: var(--ink); border-color: var(--ink); color: var(--panel);
          font-weight: 500;
        }

        .rule-label {
          display: flex; align-items: center; gap: 10px;
          font-size: 10px; letter-spacing: 0.18em; text-transform: uppercase;
          color: var(--ink-faint);
        }
        .rule-label::after {
          content: ''; flex: 1; height: 1px; background: var(--rule);
        }

        @media (prefers-reduced-motion: reduce) {
          .sel-glow, .sel-outline, .panel-accent, .list-card { animation: none; }
          .list-card, .list-card::before, .arrow { transition: none; }
        }
        `}
      </style>
      <svg
        ref={svgRef}
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        <defs>
          <style>
            {`
            .country { stroke: ${HAIRLINE}; stroke-width: 0.5; cursor: pointer; }
            .borders { fill: none; stroke: ${HAIRLINE}; stroke-width: 0.5; pointer-events: none; }
            .state { stroke-width: 0.35; cursor: pointer; }
            .city { stroke: ${OCEAN}; stroke-width: 1.5; cursor: pointer; }
            `}
          </style>
          <filter id="globe-shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow
              dx="0"
              dy="10"
              stdDeviation="18"
              floodColor="#6b5b43"
              floodOpacity="0.28"
            />
          </filter>
        </defs>
        <path
          d={path({ type: 'Sphere' }) || undefined}
          fill={OCEAN}
          filter="url(#globe-shadow)"
          onClick={() => {
            if (!didDragRef.current) setSelected(null);
          }}
        />
        {worldData.countries &&
          worldData.countries.features.map((f, idx) => {
            const name = (f.properties && (f.properties as any).name) as
              | string
              | undefined;
            const slug = name ? countryRegionSlug(name) : undefined;
            const dimmed = !!selected && slug !== selected;

            let fill = LAND;
            if (name === 'United States of America') fill = LAND_USA;
            if (slug) {
              const key =
                COUNTRY_NAME_MAP[name!] && getLinksForCountry(name!)
                  ? COUNTRY_NAME_MAP[name!]
                  : getRegionForCountry(name!);
              if (key) fill = getColorForRegion(key);
            }
            const strokeColor = slug ? fill : HAIRLINE;

            return (
              <path
                key={idx}
                className="country"
                d={path(f) || undefined}
                fill={dimmed ? DIM_FILL : fill}
                stroke={dimmed ? DIM_STROKE : strokeColor}
                onClick={(e) => {
                  e.stopPropagation();
                  handleClick('country', name || '');
                }}
              />
            );
          })}
        {worldData.borders && (
          <path className="borders" d={path(worldData.borders) || undefined} />
        )}

        {/* US States overlay */}
        {worldData.usStates &&
          worldData.usStates.features.map((sf, idx) => {
            const stateName = (sf.properties && (sf.properties as any).name) as
              | string
              | undefined;
            const regionForState = getRegionForState(stateName || '');
            const slug = regionForState ? slugify(regionForState) : undefined;
            const dimmed = !!selected && slug !== selected;
            const fill = regionForState
              ? getColorForRegion(regionForState)
              : 'transparent';
            const strokeColor = regionForState
              ? getColorForRegion(regionForState)
              : HAIRLINE;

            return (
              <path
                key={`state-${idx}`}
                className="state"
                d={path(sf) || undefined}
                fill={dimmed && regionForState ? DIM_FILL : fill}
                stroke={dimmed && regionForState ? DIM_STROKE : strokeColor}
                onClick={(e) => {
                  e.stopPropagation();
                  handleClick('state', stateName || '');
                }}
              />
            );
          })}

        {/* Selection highlight - dissolved region outline, glow + marching ants */}
        {selectedShape && (
          <g>
            <path className="sel-glow" d={path(selectedShape) || undefined} />
            <path className="sel-outline" d={path(selectedShape) || undefined} />
          </g>
        )}

        {/* City markers */}
        {CITIES.map((c, i) => {
          const [x, y] = projection([c.lon, c.lat]) || [0, 0];
          const slug = slugify(c.name);
          const isSelectedCity = selected === slug;
          const dimmed = !!selected && !isSelectedCity;
          return (
            <g key={`city-${i}`} transform={`translate(${x},${y})`}>
              {/* Invisible larger click area */}
              <circle
                r={12}
                fill="transparent"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClick('city', c.name);
                }}
                style={{ cursor: 'pointer' }}
              />
              {isSelectedCity && (
                <circle fill="none" stroke={HIGHLIGHT} strokeWidth={2.5}>
                  <animate
                    attributeName="r"
                    values="8;26"
                    dur="1.6s"
                    repeatCount="indefinite"
                  />
                  <animate
                    attributeName="opacity"
                    values="0.9;0"
                    dur="1.6s"
                    repeatCount="indefinite"
                  />
                </circle>
              )}
              {/* Visible city marker */}
              <circle
                className="city"
                r={isSelectedCity ? 8 : 6}
                fill={dimmed ? DIM_STROKE : isSelectedCity ? HIGHLIGHT : ACCENT}
                onClick={(e) => {
                  e.stopPropagation();
                  handleClick('city', c.name);
                }}
              />
            </g>
          );
        })}
      </svg>
      {/* Panel — always present. The × returns to the welcome pane. */}
      <aside
        style={{
          position: 'fixed',
          ...(isMobile
            ? {
                bottom: 0,
                left: 0,
                right: 0,
                height: Math.round(height * (1 - MOBILE_MAP_FRACTION)),
                borderTop: `1px solid ${RULE}`,
                boxShadow: '0 -18px 40px -28px rgba(60, 48, 30, 0.55)',
                padding: '22px 22px 0',
              }
            : {
                top: 0,
                right: 0,
                width: PANEL_WIDTH,
                height: '100vh',
                borderLeft: `1px solid ${RULE}`,
                boxShadow: '-18px 0 40px -30px rgba(60, 48, 30, 0.55)',
                padding: '34px 28px 0',
              }),
          background: PANEL,
          color: INK,
          boxSizing: 'border-box',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Animated accent bar — only while a region is selected */}
        {region && (
          <div
            className="panel-accent"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '3px',
            }}
          />
        )}

        <header
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: '14px',
            marginBottom: '24px',
          }}
        >
          {/* flex:1 so the rule after the label spans consistently, rather than
              collapsing to a stub behind a short title like "Japan" */}
          <div style={{ minWidth: 0, flex: 1 }}>
            {region && (
              <div className="rule-label" style={{ marginBottom: '10px' }}>
                {KIND_LABELS[region.kind]}
              </div>
            )}
            <h1
              className="display"
              style={{
                margin: 0,
                // Long names ("Mediterranean") would otherwise break mid-word
                fontSize: (region?.name.length ?? 0) > 11
                  ? isMobile
                    ? '24px'
                    : '26px'
                  : isMobile
                    ? '29px'
                    : '31px',
                fontWeight: 600,
                lineHeight: 1.1,
                display: 'flex',
                alignItems: 'baseline',
                gap: '11px',
                overflowWrap: 'anywhere',
              }}
            >
              {region && (
                <span
                  style={{
                    width: '13px',
                    height: '13px',
                    borderRadius: '50%',
                    flexShrink: 0,
                    background: getColorForRegion(region.name),
                    boxShadow: `0 0 0 3px ${HIGHLIGHT}`,
                  }}
                />
              )}
              {region ? (
                region.name
              ) : (
                <span>
                  robby<span style={{ color: ACCENT }}>.</span>food
                </span>
              )}
            </h1>
          </div>
          {region && (
            <button
              className="clear-btn"
              aria-label="Clear selection"
              onClick={() => setSelected(null)}
            >
              ×
            </button>
          )}
        </header>

        <div className="panel-scroll">
        {region ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              flexShrink: 0,
            }}
          >
            <a
              className="list-card"
              href={region.links.try}
              target="_blank"
              rel="noreferrer"
              style={
                {
                  '--card-accent': ACCENT,
                  animationDelay: '0.02s',
                } as React.CSSProperties
              }
            >
              <span className="eyebrow">Still to come</span>
              <span className="card-title display">Try List</span>
              <span className="card-sub">Places I want to eat at</span>
              <span className="arrow">→</span>
            </a>
            <a
              className="list-card"
              href={region.links.true}
              target="_blank"
              rel="noreferrer"
              style={
                {
                  '--card-accent': GREEN,
                  animationDelay: '0.09s',
                } as React.CSSProperties
              }
            >
              <span className="eyebrow">Tried &amp; true</span>
              <span className="card-title display">True List</span>
              <span className="card-sub">Places I've tried and liked</span>
              <span className="arrow">→</span>
            </a>
            <p
              style={{
                margin: '6px 0 0',
                fontSize: '12px',
                lineHeight: 1.5,
                color: INK_FAINT,
              }}
            >
              This page's link now points at {region.name} — copy it to share
              this exact view.
            </p>
          </div>
        ) : (
          <div className="panel-body" style={{ flexShrink: 0 }}>
            <p style={{ marginTop: 0 }}>
              Everywhere I've eaten well, and everywhere I still mean to. Spin
              the globe and pick a region.
            </p>

            <div className="rule-label" style={{ margin: '24px 0 12px' }}>
              The two lists
            </div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              <li style={{ display: 'flex', gap: '10px' }}>
                <span
                  aria-hidden
                  style={{
                    marginTop: '7px',
                    width: '7px',
                    height: '7px',
                    flexShrink: 0,
                    borderRadius: '50%',
                    background: ACCENT,
                  }}
                />
                <span>
                  <strong>Try</strong> — places I want to eat at
                </span>
              </li>
              <li style={{ display: 'flex', gap: '10px' }}>
                <span
                  aria-hidden
                  style={{
                    marginTop: '7px',
                    width: '7px',
                    height: '7px',
                    flexShrink: 0,
                    borderRadius: '50%',
                    background: GREEN,
                  }}
                />
                <span>
                  <strong>True</strong> — places I've tried and liked
                </span>
              </li>
            </ul>

            <div className="rule-label" style={{ margin: '24px 0 12px' }}>
              How it's split
            </div>
            <p style={{ margin: 0 }}>
              Dense cities get their own list (Seattle, Portland, the Bay Area,
              NYC). The US is carved into regions. A few countries stand alone;
              the rest are bundled by neighbourhood of the world.
            </p>

            <div className="rule-label" style={{ margin: '24px 0 12px' }}>
              Fine print
            </div>
            <p style={{ margin: 0, fontSize: '13.5px' }}>
              Some true spots are old and may have slipped or closed. And a few
              weren't even that good — they just landed at the right moment, or
              I loved the neighbourhood, or the staff.
            </p>
          </div>
        )}

        {/* Region index — every list, always reachable without hunting the globe */}
        <nav className="region-index" aria-label="All regions">
          {KIND_GROUPS.map((group) => {
            const items = REGIONS.filter((r) => r.kind === group.kind);
            if (!items.length) return null;
            return (
              <div key={group.kind}>
                <div className="chip-group-label">{group.label}</div>
                <div className="chip-row">
                  {items.map((r) => (
                    <button
                      key={r.slug}
                      className="chip"
                      aria-current={selected === r.slug}
                      onClick={() => selectRegion(r.slug)}
                      style={
                        {
                          '--chip-color': getColorForRegion(r.name),
                        } as React.CSSProperties
                      }
                    >
                      {r.name}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>
        </div>
      </aside>
    </div>
  );
}
